import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

// Native form submission endpoint. First submission triggers an activation
// email to the target address; after clicking the activation link once, all
// subsequent submissions will be delivered normally.
const FORMSUBMIT_URL = "https://formsubmit.co/corpusjurieslawchambers@gmail.com";

const legalIssues = [
  "Criminal Law", "Civil Law", "Family Law", "Property / Real Estate Law",
  "Corporate / Business Law", "Labour Law", "Banking & Finance Law",
  "Constitutional Law", "Intellectual Property Rights", "Other",
];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", legal_issue: "", message: "" });

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  // After native form submission + FormSubmit redirect, we return to the page
  // with #contact?submitted=1 — show a confirmation toast and clean the URL.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("submitted") === "1") {
        toast.success("Your inquiry has been sent. We will get back to you shortly.");
        params.delete("submitted");
        const qs = params.toString();
        const clean = window.location.pathname + (qs ? `?${qs}` : "") + "#contact";
        window.history.replaceState({}, "", clean);
      }
    } catch (e) { /* ignore */ }
  }, []);

  const validate = (e) => {
    if (!form.name || !form.phone || !form.email || !form.legal_issue || !form.message) {
      e.preventDefault();
      toast.error("Please fill in all fields before submitting.");
      return false;
    }
    return true;
  };

  const returnUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}?submitted=1#contact`
      : "https://corpusjuris.live/?submitted=1#contact";

  return (
    <section id="contact" data-testid="contact-section" className="py-28 bg-[#f7f3ea] relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#b89968]/5 rounded-full blur-3xl -translate-x-1/2" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-5 px-5 py-2 rounded-full bg-[#b89968]/10 border border-[#b89968]/20">
            <Mail className="w-3.5 h-3.5 gold-accent" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase gold-accent">Get In Touch</span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-[#0f1d3a] mb-3">
            Contact <span className="italic font-normal gold-accent">Us</span>
          </h2>
          <div className="section-gold-line mx-auto mb-6" />
          <p className="text-base leading-relaxed text-[#6a7690] max-w-2xl mx-auto">
            Schedule a consultation or inquire about our legal services. We are here to help you with your legal needs.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <h3 className="font-heading text-2xl font-semibold text-[#0f1d3a]">Reach Out to Us</h3>
            <div className="space-y-3">
              {[
                { icon: Phone, label: "Phone", value: "+91 963 3717 034", href: "tel:9633717034", testid: "contact-phone" },
                { icon: Mail, label: "Email", value: "corpusjurieslawchambers@gmail.com", href: "mailto:corpusjurieslawchambers@gmail.com", testid: "contact-email" },
              ].map(({ icon: Icon, label, value, href, testid }) => (
                <a key={label} href={href} className="flex items-center gap-4 group p-4 rounded-xl hover:bg-[#ece4d6] transition-colors" data-testid={testid}>
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#0f1d3a] group-hover:bg-[#b89968] transition-colors duration-300">
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0f1d3a]">{label}</p>
                    <p className="text-sm text-[#8892ad] mt-0.5 break-all">{value}</p>
                  </div>
                </a>
              ))}
              <div className="flex items-start gap-4 p-4 rounded-xl">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#0f1d3a]">
                  <MapPin className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0f1d3a]">Office Address</p>
                  <p className="text-sm text-[#8892ad] mt-0.5">Ovungal Bazar, Chavakkad,<br />Thrissur District, Kerala, India</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 rounded-xl">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#0f1d3a]">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-[#0f1d3a]">Office Hours</p>
                  <p className="text-sm text-[#8892ad] mt-0.5">Mon - Sat: 9:00 AM - 6:00 PM<br />Sunday: By Appointment</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form — native POST to FormSubmit (no JS fetch) */}
          <div className="lg:col-span-3">
            <form
              action={FORMSUBMIT_URL}
              method="POST"
              onSubmit={validate}
              data-testid="contact-form"
              className="bg-[#ece4d6] border border-[#d9cdb4] rounded-2xl p-8 lg:p-10 shadow-xl shadow-[#b89968]/5"
            >
              <h3 className="font-heading text-2xl font-semibold text-[#0f1d3a] mb-6">Send an Inquiry</h3>

              {/* FormSubmit configuration */}
              <input type="hidden" name="_subject" value={`New Inquiry – ${form.legal_issue || "Corpus Juris"}`} />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_next" value={returnUrl} />
              {/* Honeypot spam trap — real users never fill this */}
              <input type="text" name="_honey" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-semibold text-[#3b4a6b]">Full Name</Label>
                  <Input id="name" name="Name" data-testid="contact-input-name" placeholder="Your full name" value={form.name} onChange={(e) => handleChange("name", e.target.value)} className="rounded-xl border-[#c9bc9f] bg-[#f7f3ea] focus:ring-2 focus:ring-[#b89968]/30 focus:border-[#b89968] h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-semibold text-[#3b4a6b]">Phone Number</Label>
                  <Input id="phone" name="Phone" data-testid="contact-input-phone" placeholder="Your phone number" value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} className="rounded-xl border-[#c9bc9f] bg-[#f7f3ea] focus:ring-2 focus:ring-[#b89968]/30 focus:border-[#b89968] h-11" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold text-[#3b4a6b]">Email Address</Label>
                  <Input id="email" name="Email" type="email" data-testid="contact-input-email" placeholder="your@email.com" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className="rounded-xl border-[#c9bc9f] bg-[#f7f3ea] focus:ring-2 focus:ring-[#b89968]/30 focus:border-[#b89968] h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="legal_issue" className="text-sm font-semibold text-[#3b4a6b]">Legal Issue</Label>
                  <select
                    id="legal_issue"
                    name="Legal Issue"
                    data-testid="contact-select-issue"
                    value={form.legal_issue}
                    onChange={(e) => handleChange("legal_issue", e.target.value)}
                    className="w-full rounded-xl border border-[#c9bc9f] bg-[#f7f3ea] focus:ring-2 focus:ring-[#b89968]/30 focus:border-[#b89968] h-11 px-3 text-sm text-[#0f1d3a]"
                  >
                    <option value="" disabled>Select category</option>
                    {legalIssues.map((issue) => (
                      <option key={issue} value={issue}>{issue}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <Label htmlFor="message" className="text-sm font-semibold text-[#3b4a6b]">Your Message</Label>
                <Textarea id="message" name="Message" data-testid="contact-input-message" placeholder="Briefly describe your legal concern..." rows={4} value={form.message} onChange={(e) => handleChange("message", e.target.value)} className="rounded-xl border-[#c9bc9f] bg-[#f7f3ea] focus:ring-2 focus:ring-[#b89968]/30 focus:border-[#b89968] resize-none" />
              </div>

              <Button type="submit" data-testid="contact-submit" className="glossy-btn w-full rounded-xl bg-[#0f1d3a] text-white hover:bg-[#1f2e4a] py-3.5 text-sm font-semibold flex items-center justify-center gap-2 h-auto transition-all hover:shadow-lg tracking-wide">
                <Send className="w-4 h-4" />Submit Inquiry
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
